import { useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DropdownMenuItem,
  Label,
  Textarea,
} from '@cold-ambulance/shadcn';

type RejectMutation = {
  isPending: boolean;
  mutateAsync: (input: { id: string; reason: string }) => Promise<unknown>;
};

interface RejectApplicantDialogProps {
  applicantId: string;
  applicantName: string;
  role: 'vendor' | 'customer';
  mutation: RejectMutation;
}

export function RejectApplicantDialog({
  applicantId,
  applicantName,
  role,
  mutation,
}: RejectApplicantDialogProps) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <DropdownMenuItem
          className="text-destructive"
          onSelect={(e) => e.preventDefault()}
        >
          Reject {role}
        </DropdownMenuItem>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Reject {role} application</DialogTitle>
          <DialogDescription>
            Send a reason to <strong>{applicantName}</strong>. They will be
            notified by email and can update their application.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="reject-reason">Reason</Label>
          <Textarea
            id="reject-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain what needs to change..."
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={async () => {
              await mutation.mutateAsync({ id: applicantId, reason });
              setOpen(false);
              setReason('');
            }}
            disabled={!reason.trim() || mutation.isPending}
          >
            {mutation.isPending ? 'Rejecting...' : `Reject ${role}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
